/**
 * The refusals of an RFC being written (§5.27, Claude Design R9, kit 67's
 * Q4), put on their fields — `rfc.title`, `statement.2.text` — for the forms
 * of the app and of the account, which say the same thing in the same place.
 *
 * What is refused is `parseRuleRfc`'s answer, read the way every rule is read;
 * what is *said* is how to fix it rather than the constraint: « Write 10 to
 * 4000 characters — this one has 6. » where the validator says « text is 10 to
 * 4000 characters. ». A refused file's note on the Rules page keeps the
 * validator's own sentence — a file someone else wrote, not a field to
 * correct. A text that does not carry its level is said on Text, the field to
 * change, never on Level.
 */
import { MAX_STATEMENT_CHARS, RULE_LEVELS, parseRuleRfc, type RuleRfc } from './document';

const STATEMENT_FIELDS = ['slug', 'level', 'text', 'why', 'paths', 'status', 'check'];

export function ruleProblemsByField(doc: RuleRfc): Map<string, string> {
  const problems: string[] = [];
  parseRuleRfc(doc, '', problems);
  const out = new Map<string, string>();
  for (const problem of problems) {
    const said = problem.replace(/^: /, '');
    const twice = /^statement (\S+) is listed twice/.exec(said);
    if (twice) {
      // The second of the two is the one to rename.
      const index = doc.statements.findLastIndex((one) => one.slug === twice[1]);
      const key = `statement.${Math.max(index, 0)}.slug`;
      if (!out.has(key)) out.set(key, `Another statement of this RFC is named ${twice[1]} — rename one.`);
      continue;
    }
    const statement = /^statement (\S+): (\w+)?/.exec(said);
    if (statement) {
      const index = doc.statements.findIndex((one, at) => one.slug === statement[1] || `#${at + 1}` === statement[1]);
      const at = Math.max(index, 0);
      const rest = said.replace(/^statement \S+: /, '');
      const named = STATEMENT_FIELDS.find((name) => said.includes(` ${name} `) || said.split(': ')[1]?.startsWith(name)) ?? 'text';
      const field = /says its level/.test(rest) ? 'text' : named;
      const key = `statement.${at}.${field}`;
      if (!out.has(key)) out.set(key, statementFix(field, rest, doc.statements[at]));
      continue;
    }
    const field = ['rfc', 'slug', 'title', 'summary', 'status', 'scope', 'repos'].find((name) => said.startsWith(name)) ?? 'statements';
    if (!out.has(`rfc.${field}`)) out.set(`rfc.${field}`, rfcFix(field, said, doc));
  }
  return out;
}

/** « 1 field needs fixing » — the head of a form's summary of refusals (2B §6). */
export function fieldsToFix(count: number): string {
  return `${count} field${count === 1 ? ' needs' : 's need'} fixing`;
}

const length = (value: unknown): number => (typeof value === 'string' ? value.trim().length : 0);

/** How to fix one field of a statement, from the validator's sentence and what was typed. */
function statementFix(field: string, said: string, one: RuleRfc['statements'][number] | undefined): string {
  const level = typeof one?.level === 'string' ? one.level : '';
  const text = typeof one?.text === 'string' ? one.text : '';
  if (field === 'slug') return 'Use kebab-case: lower-case words joined by hyphens (api-errors).';
  if (field === 'level') return `Pick one of ${RULE_LEVELS.join(', ')}.`;
  if (field === 'text') {
    // Said from what was typed, not from the first sentence: a text can be refused twice, short and without its level.
    const n = length(text);
    if (!n) return `Write 10 to ${MAX_STATEMENT_CHARS} characters${level ? `, with its level in capitals: ${level}` : ''}.`;
    if (n > MAX_STATEMENT_CHARS) return `Keep it to ${MAX_STATEMENT_CHARS} characters — this one has ${n}.`;
    if (n < 10) return `Write 10 to ${MAX_STATEMENT_CHARS} characters — this one has ${n}.`;
    if (level && !text.includes(level)) {
      // Another level in capitals is the likelier slip: both are said. Longest first, so MUST NOT is not read as MUST.
      const other = [...RULE_LEVELS].sort((a, b) => b.length - a.length).find((x) => x !== level && text.includes(x));
      return other
        ? `The text says ${other} and the level is ${level} — write ${level} in the text, or pick ${other}.`
        : `Write its level in capitals in the text: ${level}.`;
    }
    return said;
  }
  if (field === 'why') return `Keep it to ${MAX_STATEMENT_CHARS} characters.`;
  if (field === 'check') return 'Name a Python script of the rules repository: checks/<name>.py, in kebab-case.';
  // Paths and status keep the validator's words: they already say what to write.
  return said;
}

/** How to fix one field of the RFC itself. */
function rfcFix(field: string, said: string, doc: RuleRfc): string {
  if (field === 'summary') {
    const n = length(doc.summary);
    return n > 2000 ? `Keep it to 2000 characters — this one has ${n}.` : `Write 10 to 2000 characters — this one has ${n}.`;
  }
  if (field === 'title') {
    const n = length(doc.title);
    return n > 200 ? `Keep it to 200 characters — this one has ${n}.` : `Write 3 to 200 characters — this one has ${n}.`;
  }
  if (field === 'slug') return 'Use kebab-case: lower-case words joined by hyphens (api-errors).';
  if (field === 'rfc') return 'Use a whole number from 1.';
  if (field === 'statements' && /non-empty/.test(said)) return 'Add a statement — an RFC holds at least one.';
  return said;
}
